import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentPasteOutlinedIcon from '@mui/icons-material/ContentPasteOutlined';
import GuideCallout, { GuideCalloutOption } from '../shared/GuideCallout';
import HistoryBookIcon from '../icons/HistoryBookIcon';
import MicrosoftExcelIcon from '../icons/MicrosoftExcelIcon';
import { PASTE_ACTION } from '../shared/SheetPaste';
import { fetchWatchlistM365Status } from '../../utils/watchlistM365Api';

/**
 * Speech-bubble guide for a new user's empty Watchlist. It points at the
 * Watchlist actions in the top bar and offers the ways to start; Paste from
 * Excel shows when `onPaste` is given. Upload and Excel 365 open Watchlist
 * History, whose header holds the import buttons; Excel 365 shows only when
 * Linked Excel is enabled and configured.
 */
export default function WatchlistGetStartedCallout({
    open,
    anchorEl,
    onUpload,
    onPaste = null,
    onAddSecurity,
    addSecurityDisabled = false,
    onDismiss,
}) {
    const [excelAvailable, setExcelAvailable] = useState(false);

    useEffect(() => {
        if (!open) return undefined;
        const controller = new AbortController();
        fetchWatchlistM365Status({ signal: controller.signal })
            .then((status) => setExcelAvailable(
                status?.enabled === true && status?.configured === true,
            ))
            .catch(() => {});
        return () => controller.abort();
    }, [open]);

    return (
        <GuideCallout
            open={open}
            anchorEl={anchorEl}
            ariaLabel="Get started with your Watchlist"
            title="Add your first securities"
            subtitle="Pick a way to start — Watchlist imports are up here."
            onDismiss={onDismiss}
        >
            <Box sx={{ display: 'grid', gap: 0.25 }}>
                <GuideCalloutOption
                    icon={<HistoryBookIcon sx={{ fontSize: 20 }} />}
                    label="Upload a file"
                    detail="Import a Watchlist spreadsheet"
                    onClick={onUpload}
                />
                {onPaste && (
                    <GuideCalloutOption
                        icon={<ContentPasteOutlinedIcon sx={{ fontSize: 20 }} />}
                        label="Paste from Excel"
                        detail={`Copy your sheet with its header row, then ${PASTE_ACTION}`}
                        onClick={onPaste}
                    />
                )}
                {excelAvailable && (
                    <GuideCalloutOption
                        icon={<MicrosoftExcelIcon sx={{ fontSize: 18 }} />}
                        label="Sync with Excel 365"
                        detail="Link a Watchlist worksheet"
                        onClick={onUpload}
                    />
                )}
                <GuideCalloutOption
                    icon={<AddIcon sx={{ fontSize: 20 }} />}
                    label="Add securities"
                    detail="Search and add them one at a time"
                    onClick={onAddSecurity}
                    disabled={addSecurityDisabled}
                />
            </Box>
        </GuideCallout>
    );
}

WatchlistGetStartedCallout.propTypes = {
    open: PropTypes.bool.isRequired,
    anchorEl: PropTypes.object,
    onUpload: PropTypes.func.isRequired,
    onPaste: PropTypes.func,
    onAddSecurity: PropTypes.func.isRequired,
    addSecurityDisabled: PropTypes.bool,
    onDismiss: PropTypes.func,
};
